import { useMemo } from 'react';

import { getClient } from '@zg-rentals/ts-client-http-client';
import { LessenPilotEligibilityState } from '@zg-rentals/ts-maintenance-tracker-client';
import { getClient as getMaintenanceTrackerClient } from '@zg-rentals/ts-maintenance-tracker-client/client';
import { useMaintenanceTrackerLessenWaitlistWebApiHandler_status } from '@zg-rentals/ts-maintenance-tracker-client-react-query';

import { LESSEN_WAITLIST_STATUS_TAG } from '../constants';

export type UseLessenMaintenanceNavStateParams = {
  loggedIn?: boolean;
  /** Returns this value verbatim and skips the status call. */
  override?: boolean;
};

const ENABLED_STATES = new Set<LessenPilotEligibilityState>([
  LessenPilotEligibilityState.WAITLIST,
  LessenPilotEligibilityState.LAUNCHED,
]);

/**
 * Gate for the Lessen "Maintenance" nav item. Visibility comes from maintenance-tracker's waitlist
 * status API rather than authCheck, so the pilot's zip and allowlist rules live in one place and the
 * nav and the maintenance page share a single cached request.
 *
 * Most hosts never call `setClient` for the maintenance-tracker client, so the hook falls back to a
 * relative `/rental/` prefix. That resolves correctly wherever the nav is served from a Zillow host;
 * on localhost dev of other hosts the call 404s and the item simply stays hidden.
 */
export const useLessenMaintenanceNavState = ({ loggedIn, override }: UseLessenMaintenanceNavStateParams = {}) => {
  const httpClient = useMemo(() => getMaintenanceTrackerClient() ?? getClient().extend({ prefixUrl: '/rental/' }), []);

  const isQueryEnabled = override === undefined && Boolean(loggedIn);

  const { data, isPending } = useMaintenanceTrackerLessenWaitlistWebApiHandler_status({
    httpClient,
    enabled: isQueryEnabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
    meta: {
      tags: [LESSEN_WAITLIST_STATUS_TAG],
      context: { api: 'lessenWaitlistStatus', details: {} },
    },
  });

  if (override !== undefined) {
    return { isEnabled: override, isLoading: false, state: undefined };
  }

  return {
    isEnabled: Boolean(loggedIn) && data !== undefined && ENABLED_STATES.has(data.state),
    isLoading: isQueryEnabled && isPending,
    state: data?.state,
  };
};

export default useLessenMaintenanceNavState;
